import notify from 'devextreme/ui/notify';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getItemApi, postApi } from '../apiUtils';
import { Platform } from '../components/loginForm/LoginForm';
import { clearLocalSession } from '../hooks/useSessionStorage';
import { ApplicationUser } from '../types/applicationUser.types';
import { API_URL } from '../utils/apiUrl';
import { useAssetClass } from './assetClass';
import { useConfig } from './configContext';

const AuthContext = createContext<AuthContextType>({ user: null, loading: false, entryUrl: null });

function AuthProvider(props: any) {
  const [user, setUser] = useState<ApplicationUser | null>();
  const [entryUrl, setEntryUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { changeAssetClass } = useAssetClass();
  const { setDefaultConfigToStorage } = useConfig();

  useEffect(() => {
    (async function () {
      await getItemApi({
        url: `${API_URL.IS_USER_AUTHENTICATED}`,
        params: {},
        callAfterSuccess: async (result: boolean) => {
          if (result) {
            await getItemApi<ApplicationUser>({
              url: `${API_URL.GET_CURRENT_USER}`,
              params: {},
              callAfterSuccess: async (user: ApplicationUser) => {
                // Pokud má uživatel jinou platformu než DESKTOP, musíme ji změnit
                if (user.platform !== Platform.DESKTOP) {
                  await postApi<ApplicationUser>({
                    url: `${API_URL.CHANGE_CURRENT_PLATFORM}`,
                    data: { platform: Platform.DESKTOP },
                    callAfterSuccess: () => {
                      user.platform = Platform.DESKTOP;
                    },
                  });
                }

                setUser(user);
                if (changeAssetClass && user.assetClassId !== null)
                  changeAssetClass(user.assetClassId);
              },
              hideNotifications: true,
            });
          }
        },
        hideNotifications: true,
      });
      setLoading(false);
    })();
  }, []);

  const signIn = useCallback(
    async (
      username: string,
      password: string,
      uid: string,
      assetClassId: number,
      platform: string,
      errorMessage: string,
    ): Promise<void> => {
      if (uid) {
        await postApi<ApplicationUser>({
          url: `${API_URL.LOGIN_UID}`,
          data: { uId: uid, assetClassId, platform },
          callAfterSuccess: (result) => {
            setUser(result);
            if (changeAssetClass) changeAssetClass(result.assetClassId);
            clearLocalSession();
            setDefaultConfigToStorage!();
          },
          callAfterError: () => {
            notify(errorMessage, 'error');
          },
        });
        return;
      }

      await postApi<ApplicationUser>({
        url: `${API_URL.LOGIN}`,
        data: { login: username, password, assetClassId, platform },
        ignoreUnAuthorizedError: true,
        callAfterSuccess: (result) => {
          setUser(result);
          if (changeAssetClass) changeAssetClass(result.assetClassId);
          clearLocalSession();
          setDefaultConfigToStorage!();
        },
        callAfterError: () => {
          notify(errorMessage, 'error');
        },
      });
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await postApi<ApplicationUser>({
      url: `${API_URL.LOGOUT}`,
      data: { logout: true },
      callAfterSuccess: () => {
        setEntryUrl(null);
        setUser(null);
      },
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, signIn, signOut, loading, entryUrl, setEntryUrl }}
      {...props}
    />
  );
}

interface AuthContextType {
  user: ApplicationUser | null;
  loading: boolean;
  entryUrl: string | null;
  signIn?: (
    email: string,
    password: string,
    uid: string,
    assetClassId: number,
    platform: string,
    errorMessage: string,
  ) => Promise<void>;
  signOut?: () => Promise<void>;
  setEntryUrl?: (entryUrl: string | null) => void;
}

const useAuth = () => useContext<AuthContextType>(AuthContext);

export { AuthProvider, useAuth };
